import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import SpanishDatePicker from "@/components/SpanishDatePicker";
import PlayerCombobox from "@/components/PlayerCombobox";
import PhysioSlotCard from "@/components/physio/PhysioSlotCard";
import PhysioAppointmentDialog from "@/components/physio/PhysioAppointmentDialog";
import PhysioConfigDialog from "@/components/physio/PhysioConfigDialog";
import PhysioBlockDialog from "@/components/physio/PhysioBlockDialog";
import PhysioHistoryDialog from "@/components/physio/PhysioHistoryDialog";
import PhysioAuditDialog from "@/components/physio/PhysioAuditDialog";
import { PHYSIO_STATUS, WEEKDAY_NAMES, isoToday, mondayOf, addDays, fmtDateEs, isoWeekNumber } from "@/lib/physio";
import {
  Plus, FileDown, Settings, ClipboardList, ChevronLeft, ChevronRight,
  CalendarDays, HeartPulse, RotateCcw, Filter,
} from "lucide-react";

export default function Physio() {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [viewMode, setViewMode] = useState("day"); // day | week
  const [selectedDate, setSelectedDate] = useState(isoToday());
  const [agenda, setAgenda] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filters, setFilters] = useState({ team_id: "all", player_id: "", status: "all" });

  // Dialog state
  const [apptDialog, setApptDialog] = useState({ open: false, initial: null });
  const [configOpen, setConfigOpen] = useState(false);
  const [blockDialog, setBlockDialog] = useState({ open: false, initial: null });
  const [historyDialog, setHistoryDialog] = useState({ open: false, playerId: null });
  const [auditOpen, setAuditOpen] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState({ open: false, appt: null, reason: "" });

  const range = useMemo(() => {
    if (viewMode === "week") {
      const mon = mondayOf(selectedDate);
      return { from: mon, to: addDays(mon, 6) };
    }
    return { from: selectedDate, to: selectedDate };
  }, [viewMode, selectedDate]);

  useEffect(() => {
    Promise.all([
      api.get("/physio/config"),
      api.get("/teams"),
      api.get("/players"),
    ]).then(([c, t, p]) => {
      setConfig(c.data);
      setTeams(t.data || []);
      setPlayers(p.data || []);
    }).catch(() => toast.error("No se pudo cargar la configuración"));
  }, []);

  const loadAgenda = useCallback(() => {
    setLoading(true);
    setError(false);
    api.get(`/physio/agenda?date_from=${range.from}&date_to=${range.to}`)
      .then((r) => setAgenda(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [range.from, range.to]);

  useEffect(() => { loadAgenda(); }, [loadAgenda]);

  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));
  const clearFilters = () => setFilters({ team_id: "all", player_id: "", status: "all" });
  const hasFilters = filters.team_id !== "all" || filters.player_id || filters.status !== "all";
  const teamPlayers = filters.team_id !== "all" ? players.filter((p) => p.team_id === filters.team_id) : players;

  const slotVisible = (slot) => {
    if (filters.status !== "all") return slot.status === filters.status;
    if (filters.team_id !== "all" && slot.appointment?.team_id_at_booking !== filters.team_id) return false;
    if (filters.player_id && slot.appointment?.player_id !== filters.player_id) return false;
    if ((filters.team_id !== "all" || filters.player_id) && !slot.appointment) return false;
    return true;
  };

  const doStatus = async (appt, status, reason = "") => {
    try {
      await api.post(`/physio/appointments/${appt.id}/status`, { status, cancellation_reason: reason });
      toast.success(status === "cancelled" ? "Cita cancelada" : "Cita actualizada");
      loadAgenda();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar");
    }
  };

  const onAction = (action, payload) => {
    if (action === "edit") setApptDialog({ open: true, initial: payload });
    else if (action === "cancelled") setConfirmCancel({ open: true, appt: payload, reason: "" });
    else if (action === "unblock") deleteBlock(payload);
    else doStatus(payload, action);
  };

  const deleteBlock = async (block) => {
    try {
      await api.delete(`/physio/blocks/${block.id}`);
      toast.success("Horario desbloqueado");
      loadAgenda();
    } catch { toast.error("No se pudo desbloquear"); }
  };

  const exportPdf = async (kind) => {
    const path = kind === "day"
      ? `/physio/daily-pdf?date=${selectedDate}`
      : `/physio/weekly-pdf?date=${selectedDate}`;
    const { downloadPhysioPdf } = await import("@/lib/export");
    downloadPhysioPdf(path, kind === "day" ? `fisioterapia_dia_${selectedDate}.pdf` : `fisioterapia_semana_${mondayOf(selectedDate)}.pdf`);
  };

  const attentionDays = (agenda?.days || []).filter((d) => d.is_attention_day && d.slots.length > 0);
  const displayDays = viewMode === "week" ? attentionDays : (agenda?.days || []);

  return (
    <div className="space-y-5" data-testid="physio-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-black flex items-center gap-2" style={{ fontFamily: "Nunito" }}>
            <HeartPulse className="h-7 w-7 text-purple-700" /> Fisioterapia
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Hoy {fmtDateEs(agenda?.today || isoToday())}
            {agenda?.next_attention_day && ` · Próximo día de atención: ${fmtDateEs(agenda.next_attention_day)}`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={() => setApptDialog({ open: true, initial: { date: selectedDate } })}
            className="bg-purple-700 hover:bg-purple-800" data-testid="physio-new-appointment">
            <Plus className="h-4 w-4 mr-1" /> Nueva cita
          </Button>
          <Button variant="outline" onClick={() => exportPdf("day")} data-testid="physio-pdf-daily">
            <FileDown className="h-4 w-4 mr-1" /> PDF diario
          </Button>
          <Button variant="outline" onClick={() => exportPdf("week")} data-testid="physio-pdf-weekly">
            <FileDown className="h-4 w-4 mr-1" /> PDF semanal
          </Button>
          <Button variant="outline" onClick={() => setConfigOpen(true)} data-testid="physio-config-open" title="Configurar">
            <Settings className="h-4 w-4" />
          </Button>
          <Button variant="outline" onClick={() => setAuditOpen(true)} data-testid="physio-audit-open" title="Auditoría">
            <ClipboardList className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Controls */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded-lg border border-slate-200 p-0.5">
              <button onClick={() => setViewMode("day")} data-testid="physio-view-day"
                className={`px-3 py-1.5 text-sm font-bold rounded-md ${viewMode === "day" ? "bg-purple-700 text-white" : "text-slate-600"}`}>
                Vista diaria
              </button>
              <button onClick={() => setViewMode("week")} data-testid="physio-view-week"
                className={`px-3 py-1.5 text-sm font-bold rounded-md ${viewMode === "week" ? "bg-purple-700 text-white" : "text-slate-600"}`}>
                Vista semanal
              </button>
            </div>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="outline" onClick={() => setSelectedDate(addDays(selectedDate, -7))} data-testid="physio-week-prev">
                <ChevronLeft className="h-4 w-4" /> Semana anterior
              </Button>
              <Button size="sm" variant="outline" onClick={() => setSelectedDate(isoToday())} data-testid="physio-week-current">
                <CalendarDays className="h-4 w-4 mr-1" /> Esta semana
              </Button>
              <Button size="sm" variant="outline" onClick={() => setSelectedDate(addDays(selectedDate, 7))} data-testid="physio-week-next">
                Semana siguiente <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="w-44"><SpanishDatePicker value={selectedDate} onChange={(v) => v && setSelectedDate(v)} testId="physio-date-picker" /></div>
            {viewMode === "week" && (
              <span className="text-sm text-slate-500">Semana {isoWeekNumber(selectedDate)} · {fmtDateEs(mondayOf(selectedDate))} → {fmtDateEs(addDays(mondayOf(selectedDate), 6))}</span>
            )}
          </div>

          {/* Filters */}
          <div className="flex items-end gap-2 flex-wrap border-t border-slate-100 pt-3">
            <div className="flex items-center gap-1 text-xs font-bold text-slate-500"><Filter className="h-3.5 w-3.5" /> Filtros</div>
            <div>
              <Label className="text-xs">Equipo</Label>
              <Select value={filters.team_id} onValueChange={(v) => setFilters((f) => ({ ...f, team_id: v, player_id: "" }))}>
                <SelectTrigger className="w-48 h-9" data-testid="physio-filter-team"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los equipos</SelectItem>
                  {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}{t.category ? ` · ${t.category}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Jugador</Label>
              <PlayerCombobox players={teamPlayers} value={filters.player_id} onChange={set("player_id")}
                testId="physio-filter-player" className="w-48" placeholder="Todos" />
            </div>
            <div>
              <Label className="text-xs">Estado</Label>
              <Select value={filters.status} onValueChange={set("status")}>
                <SelectTrigger className="w-44 h-9" data-testid="physio-filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {["available", "booked", "confirmed", "completed", "no_show", "blocked", "cancelled"].map((k) => (
                    <SelectItem key={k} value={k}>{PHYSIO_STATUS[k]?.label || k}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {hasFilters && (
              <Button size="sm" variant="ghost" onClick={clearFilters} data-testid="physio-clear-filters">
                <RotateCcw className="h-4 w-4 mr-1" /> Limpiar filtros
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Agenda */}
      {loading && <div className="text-center py-10 text-slate-500" data-testid="physio-loading">Cargando agenda…</div>}
      {error && (
        <div className="text-center py-10" data-testid="physio-error">
          <p className="text-rose-600 mb-2">No se pudo cargar la agenda.</p>
          <Button variant="outline" onClick={loadAgenda} data-testid="physio-retry"><RotateCcw className="h-4 w-4 mr-1" /> Reintentar</Button>
        </div>
      )}

      {!loading && !error && agenda && (
        <div className={viewMode === "week" ? "grid gap-4 md:grid-cols-2" : "space-y-4"}>
          {displayDays.length === 0 && viewMode === "week" && (
            <div className="text-slate-500 text-sm">No hay días de atención en esta semana.</div>
          )}
          {displayDays.map((day) => {
            const visSlots = day.slots.filter(slotVisible);
            const showCancelled = filters.status === "all" || filters.status === "cancelled";
            const cancelled = showCancelled ? (day.cancelled || []) : [];
            return (
              <div key={day.date} data-testid={`physio-day-${day.date}`}>
                <div className="flex items-center gap-2 mb-2">
                  <h2 className="text-lg font-black" style={{ fontFamily: "Nunito" }}>
                    {day.weekday_label.charAt(0).toUpperCase() + day.weekday_label.slice(1)} · {fmtDateEs(day.date)}
                  </h2>
                  {day.is_extra && <span className="text-[10px] font-bold uppercase bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Apertura extra</span>}
                </div>
                {!day.is_attention_day && !day.is_extra ? (
                  <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500" data-testid={`physio-no-agenda-${day.date}`}>
                    No hay agenda ordinaria de fisioterapia para esta fecha.
                  </div>
                ) : (
                  <>
                    <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                      {visSlots.map((slot) => (
                        <PhysioSlotCard
                          key={`${day.date}-${slot.start}`}
                          slot={slot}
                          onReserve={() => setApptDialog({ open: true, initial: { date: day.date, start_time: slot.start } })}
                          onBlock={() => setBlockDialog({ open: true, initial: { date: day.date, start_time: slot.start, end_time: slot.end } })}
                          onAction={onAction}
                          onHistory={(pid) => setHistoryDialog({ open: true, playerId: pid })}
                        />
                      ))}
                      {visSlots.length === 0 && <div className="text-sm text-slate-400 col-span-full py-3">Sin turnos que coincidan con los filtros.</div>}
                    </div>
                    {cancelled.length > 0 && (
                      <div className="mt-3 rounded-lg bg-rose-50 border border-rose-100 p-3">
                        <div className="text-xs font-bold text-rose-700 mb-1">Canceladas ({cancelled.length})</div>
                        {cancelled.map((a) => (
                          <div key={a.id} className="text-xs text-rose-800/80">
                            {a.start_time}–{a.end_time} · {a.player_name} ({a.team_name})
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Dialogs */}
      <PhysioAppointmentDialog
        open={apptDialog.open}
        onOpenChange={(o) => setApptDialog((d) => ({ ...d, open: o }))}
        teams={teams} players={players} config={config}
        initial={apptDialog.initial}
        onSaved={loadAgenda}
      />
      <PhysioConfigDialog open={configOpen} onOpenChange={setConfigOpen} config={config}
        onSaved={() => { api.get("/physio/config").then((r) => setConfig(r.data)); loadAgenda(); }} />
      <PhysioBlockDialog open={blockDialog.open} onOpenChange={(o) => setBlockDialog((d) => ({ ...d, open: o }))}
        initial={blockDialog.initial} onSaved={loadAgenda} />
      <PhysioHistoryDialog open={historyDialog.open} onOpenChange={(o) => setHistoryDialog((d) => ({ ...d, open: o }))}
        playerId={historyDialog.playerId} />
      <PhysioAuditDialog open={auditOpen} onOpenChange={setAuditOpen} />

      <AlertDialog open={confirmCancel.open} onOpenChange={(o) => setConfirmCancel((c) => ({ ...c, open: o }))}>
        <AlertDialogContent data-testid="physio-cancel-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Cancelar esta cita?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmCancel.appt && `${confirmCancel.appt.player_name} · ${fmtDateEs(confirmCancel.appt.date)} · ${confirmCancel.appt.start_time}. `}
              La cita se conserva en el historial y el turno queda libre.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input placeholder="Motivo de la cancelación (opcional)"
            value={confirmCancel.reason} onChange={(e) => setConfirmCancel((c) => ({ ...c, reason: e.target.value }))}
            data-testid="physio-cancel-reason" />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="physio-cancel-abort">Volver</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" data-testid="physio-cancel-accept"
              onClick={() => { doStatus(confirmCancel.appt, "cancelled", confirmCancel.reason); setConfirmCancel({ open: false, appt: null, reason: "" }); }}>
              Cancelar cita
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
